import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Input, Label } from 'reactstrap';
import cx from 'classnames';
import {
  Button,
  SelectFieldAsyncPaginate,
  SelectFieldv2,
  SearchField,
} from 'components';
import StatusBadge, {
  type StatusBadgeName,
} from 'components/Storyteller/StatusBadge/StatusBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileImport,
  faPlus,
  faSearch,
} from '@fortawesome/pro-regular-svg-icons';
import { debounce } from 'shared/helpers/debounce';
import formatDisplayCount from 'shared/helpers/formatDisplayCount';
import { MultiValue, MultiValueProps, SingleValue } from 'react-select';
import getGlobalFilterOptionURLParam from 'components/v2/DataTable/helpers/getGlobalFilterOptionURLParam';
import useURLParams from 'components/v2/DataTable/helpers/useURLParams';
import URL_PARAMS from 'components/v2/DataTable/data/urlParams';
import { usePasswordManagerInputProps } from 'infrastructure/form/PasswordManagerAutofillContext';
import type { SelectFieldOptionObject } from '../SelectField/types';
import { LoadOptions, ReduceOptions } from '../SelectFieldAsyncPaginate';

import './TopBar.scss';

const DEFAULT_SORTING_VALUE = 'internalTitle';
const STATUS_FILTER_NAME = 'statuses';
const MAX_VISIBLE_STATUS_FILTER_BADGES = 2;

const onChange = (
  setValue: (value?: SelectFieldOptionObject) => void,
  selectedValue?: SingleValue<SelectFieldOptionObject>
): void => {
  if (selectedValue) {
    setValue(selectedValue);
  } else {
    setValue(undefined);
  }
};

interface Filter {
  label: string;
  value: string;
}

const isStatusBadgeName = (value: string): value is StatusBadgeName =>
  value === 'draft' || value === 'published' || value === 'archived';

export const sortSelectedOptionsByFilterOrder = (
  selectedOptions: SelectFieldOptionObject[],
  filters: Array<Filter> | LoadOptions<Filter>,
  topOptions: Array<Filter> = []
) => {
  if (typeof filters === 'function') {
    return selectedOptions;
  }

  const filterOrder = new Map(
    [...topOptions, ...filters].map((filter, index) => [filter.value, index])
  );

  return [...selectedOptions].sort((firstOption, secondOption) => {
    const firstOptionIndex =
      filterOrder.get(firstOption.value) ?? Number.MAX_SAFE_INTEGER;
    const secondOptionIndex =
      filterOrder.get(secondOption.value) ?? Number.MAX_SAFE_INTEGER;

    return firstOptionIndex - secondOptionIndex;
  });
};

const StatusFilterMultiValue = ({
  data,
  getValue,
  index,
}: MultiValueProps<SelectFieldOptionObject, true>) => {
  const selectedValues = getValue();

  if (index === MAX_VISIBLE_STATUS_FILTER_BADGES) {
    return (
      <span className="badge status-badge status-filter-select__overflow">
        +{selectedValues.length - MAX_VISIBLE_STATUS_FILTER_BADGES}
      </span>
    );
  }

  if (index > MAX_VISIBLE_STATUS_FILTER_BADGES) {
    return null;
  }

  return (
    <span className="status-filter-select__badge">
      {isStatusBadgeName(data.value) ? (
        <StatusBadge status={data.value} />
      ) : (
        data.label
      )}
    </span>
  );
};

const emptyFilterOptions: Array<Filter> = [];

interface SearchProps {
  searchQuery?: string;
  setSearchQuery: (query: string) => void;
  tableId: string;
  isDisabled?: boolean;
  gotoPage: (page: number) => void;
}

export const Search: React.FC<SearchProps> = ({
  searchQuery = '',
  setSearchQuery,
  tableId,
  isDisabled,
  gotoPage,
}) => {
  const passwordManagerInputProps = usePasswordManagerInputProps();
  const [shouldFocusInputOnPageLoad, setShouldFocusInputOnPageLoad] =
    useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    setSearchQuery(event.target.value);
    // Persist focus state while input is disabled
    setShouldFocusInputOnPageLoad(true);
    gotoPage(0);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedChangeHandler = useCallback(debounce(handleChange, 750), []);

  useEffect(() => {
    if (!isDisabled && shouldFocusInputOnPageLoad) {
      inputRef.current?.focus();
      setShouldFocusInputOnPageLoad(false);
    }
  }, [isDisabled, shouldFocusInputOnPageLoad]);

  return (
    <form
      className="form search-bar"
      onSubmit={event => {
        event.preventDefault();
      }}
    >
      <div className="form__form-group-field">
        <Label className="sr-only" htmlFor="tableSearch">
          Search
        </Label>
        <Input
          innerRef={inputRef}
          className="search-input"
          id="tableSearch"
          name="tableSearch"
          placeholder="Search"
          type="search"
          defaultValue={searchQuery}
          onChange={event => {
            event.persist();
            debouncedChangeHandler(event);
          }}
          data-testid={`${tableId}__searchInput`}
          disabled={isDisabled}
          {...passwordManagerInputProps}
        />
        <Button
          aria-label="Search"
          type="button"
          className="input-group-append"
          color="secondary"
          icon={<FontAwesomeIcon icon={faSearch} />}
          disabled={isDisabled}
        />
      </div>
    </form>
  );
};

interface StatusFiltersProps {
  filters: Array<Filter> | LoadOptions<Filter>;
  setFilteredOption?: (value?: SelectFieldOptionObject) => void;
  filteredOption?: SelectFieldOptionObject;
  setFilteredOptions?: (value: SelectFieldOptionObject[]) => void;
  filteredOptions?: SelectFieldOptionObject[];
  filterName?: string;
  placeholder?: string;
  tableId: string;
  isDisabled?: boolean;
  isMulti?: boolean;
  topOptions?: Array<Filter>;
  gotoPage: (page: number) => void;
  defaultOption?: SelectFieldOptionObject;
  minWidth?: string;
  width?: string;
  openMenuOnFocus?: boolean;
  clearInputValueOnFocus?: boolean;
  blurInputOnSelect?: boolean;
  cacheUniqs?: readonly unknown[];
}

export const StatusFilters: React.FC<StatusFiltersProps> = ({
  filters,
  setFilteredOption,
  filteredOption,
  setFilteredOptions,
  filteredOptions,
  filterName = '',
  placeholder = 'Search...',
  tableId,
  isDisabled,
  isMulti = false,
  topOptions = emptyFilterOptions,
  gotoPage,
  defaultOption,
  minWidth,
  width,
  openMenuOnFocus,
  clearInputValueOnFocus,
  blurInputOnSelect,
  cacheUniqs,
}) => {
  const { setURLParams } = useURLParams();
  const filterUrlParamKey = getGlobalFilterOptionURLParam(filterName);
  const onSelectFieldChange = useCallback(
    (newValue: SingleValue<SelectFieldOptionObject>) => {
      const filter = newValue?.value;
      setURLParams({
        [filterUrlParamKey]: filter || '',
      });
      if (setFilteredOption) {
        onChange(setFilteredOption, newValue);
      }
      gotoPage(0);
    },
    [filterUrlParamKey, gotoPage, setFilteredOption, setURLParams]
  );
  const onMultiSelectFieldChange = useCallback(
    (newValue: MultiValue<SelectFieldOptionObject>) => {
      const filtersValue = sortSelectedOptionsByFilterOrder(
        [...newValue],
        filters,
        topOptions
      );

      if (filtersValue.length === 0) {
        return;
      }

      setURLParams({
        [filterUrlParamKey]: filtersValue.map(filter => filter.value).join(','),
      });
      setFilteredOptions?.(filtersValue);
      gotoPage(0);
    },
    [
      filterUrlParamKey,
      filters,
      gotoPage,
      setFilteredOptions,
      setURLParams,
      topOptions,
    ]
  );

  const isAsync = typeof filters === 'function';
  const isStatusFilter =
    isMulti && filterName.toLowerCase() === STATUS_FILTER_NAME;

  const reduceOptions: ReduceOptions = useCallback(
    (prevOptions, loadedOptions) => [
      ...topOptions,
      ...prevOptions,
      ...loadedOptions,
    ],
    [topOptions]
  );

  const containerStyle = {
    ...(minWidth && { '--filter-min-width': minWidth }),
    ...(width && { '--filter-width': width }),
  } as React.CSSProperties;
  const selectOptions =
    typeof filters === 'function' ? emptyFilterOptions : filters;
  let filterSelect: React.ReactNode;

  if (isAsync) {
    filterSelect = (
      <SelectFieldAsyncPaginate
        isClearable={
          !!filteredOption &&
          defaultOption &&
          defaultOption.value !== filteredOption?.value
        }
        loadOptions={filters}
        reduceOptions={reduceOptions}
        onChange={onSelectFieldChange}
        debounceTimeout={200}
        placeholder={placeholder}
        isDisabled={isDisabled}
        clearInputValueOnFocus={clearInputValueOnFocus}
        openMenuOnFocus={openMenuOnFocus}
        blurInputOnSelect={blurInputOnSelect}
        cacheUniqs={cacheUniqs}
        value={filteredOption ?? defaultOption}
      />
    );
  } else if (isMulti) {
    filterSelect = (
      <SelectFieldv2
        onChange={onMultiSelectFieldChange}
        id={filterName}
        isClearable={false}
        isSearchable={false}
        options={selectOptions}
        selectFieldId={`${filterName}SelectFieldId`}
        isMulti
        isDisabled={isDisabled}
        controlledValue={filteredOptions}
        hideSelectedOptions={false}
        className={cx({ 'status-filter-select': isStatusFilter })}
        customMultiValueComponent={
          isStatusFilter ? StatusFilterMultiValue : undefined
        }
        isOptionDisabled={
          isStatusFilter
            ? (option, selectedOptions) =>
                selectedOptions.length === 1 &&
                selectedOptions[0]?.value === option.value
            : undefined
        }
      />
    );
  } else {
    filterSelect = (
      <SelectFieldv2
        onChange={onSelectFieldChange}
        id={filterName}
        isClearable={false}
        isSearchable={false}
        options={selectOptions}
        selectFieldId={`${filterName}SelectFieldId`}
        isMulti={false}
        isDisabled={isDisabled}
        controlledValue={filteredOption ?? defaultOption ?? selectOptions[0]}
        hideSelectedOptions
        className={cx({ 'status-filter-select': isStatusFilter })}
      />
    );
  }

  return (
    <div
      data-testid={`${tableId}__statusFilters`}
      className={cx('labeled-toggle-container', {
        'filter-dropdown': isAsync,
        'has-custom-min-width': !!minWidth,
        'has-custom-width': !!width,
        'status-filter-dropdown': isStatusFilter,
      })}
      style={containerStyle}
    >
      {filterSelect}
    </div>
  );
};

interface Option {
  label: string;
  value: string;
}

interface SortingOptionsProps {
  options: Array<Option>;
  setSortingOption: (value?: SelectFieldOptionObject) => void;
  defaultSortingOption?: string;
  sortingOption?: Option;
  isDisabled?: boolean;
  gotoPage: (page: number) => void;
}

export const SortingOptions: React.FC<SortingOptionsProps> = ({
  options,
  setSortingOption,
  sortingOption,
  defaultSortingOption = DEFAULT_SORTING_VALUE,
  isDisabled,
  gotoPage,
}) => {
  const { setURLParams } = useURLParams();
  const onSelectFieldChange = useCallback(
    (event: SingleValue<SelectFieldOptionObject>) => {
      onChange(setSortingOption, event);
      gotoPage(0);
    },
    [gotoPage, setSortingOption]
  );

  useEffect(() => {
    setURLParams({
      [URL_PARAMS.globalSortingOption]:
        sortingOption?.value || defaultSortingOption,
    });
  }, [defaultSortingOption, setURLParams, sortingOption]);

  return (
    <div className="labeled-toggle-container">
      <span className="sortingLabel">Sort by:</span>
      <div className="sortingOptionsContainer">
        <SelectFieldv2
          onChange={onSelectFieldChange}
          id="sorting"
          isClearable={false}
          isSearchable={false}
          options={options}
          isMulti={false}
          isDisabled={isDisabled}
          controlledValue={
            sortingOption || {
              value: defaultSortingOption,
              label: defaultSortingOption,
            }
          }
        />
      </div>
    </div>
  );
};

export interface SearchQueryProps {
  searchQuery?: string;
  setSearchQuery: Dispatch<SetStateAction<string>>;
}

export interface TopBarConfig {
  searchFeature?: SearchQueryProps;
  sortingFeature?: {
    sortingOption?: SelectFieldOptionObject;
    defaultSortingOption?: string;
    setSortingOption: (value?: SelectFieldOptionObject) => void;
    options: Array<Option>;
  };
  filterFeature?: Array<{
    filters: Array<Filter> | LoadOptions<Filter>;
    filteredOption?: SelectFieldOptionObject;
    filteredOptions?: SelectFieldOptionObject[];
    defaultFilterOption?: string;
    setFilteredOption?: (value?: SelectFieldOptionObject) => void;
    setFilteredOptions?: (value: SelectFieldOptionObject[]) => void;
    filterName: string;
    placeholder?: string;
    topOptions?: Array<Filter>;
    defaultOption?: SelectFieldOptionObject;
    minWidth?: string;
    width?: string;
    isMulti?: boolean;
    cacheUniqs?: readonly unknown[];
  }>;
  customButtons?: React.ReactNode;
  addButton?: {
    addButtonText: string;
    onAddButtonClick: () => void;
    id?: string;
    dataTestId?: string;
    isLoading?: boolean;
    isDisabled?: boolean;
  };
  otherButton?: {
    buttonText: string;
    onButtonClick: () => void;
  };
  isDisabled?: boolean;
}

export interface TopBarProps {
  hideResultCount?: boolean;
  topbarConfig: TopBarConfig;
  pageSize: number; // Needed for top Pagination Info, e.g. 21 to 40 of 100
  pageIndex: number; // Needed for top Pagination Info, e.g. 21 to 40 of 100
  totalElementsCount: number; // Needed for top Pagination Info, e.g. 21 to 40 of 100
  tableId: string; // e.g. for testing purposes
  gotoPage: (page: number) => void;
}

const TopBar: React.FC<TopBarProps> = ({
  hideResultCount = false,
  pageSize,
  pageIndex,
  totalElementsCount,
  tableId,
  gotoPage,
  topbarConfig: {
    searchFeature,
    filterFeature,
    sortingFeature,
    customButtons,
    addButton,
    otherButton,
    isDisabled,
  },
}) => {
  const pagesFrom = totalElementsCount === 0 ? 0 : 1 + pageSize * pageIndex;
  const pagesUntil =
    pageSize * (pageIndex + 1) > totalElementsCount
      ? totalElementsCount
      : pageSize * (pageIndex + 1);

  const addButtonTestId = {
    'data-testid': addButton?.dataTestId || 'newItemButton',
  };

  return (
    <div className={cx('topbar', { isDisabled })}>
      <div className="filter-wrapper">
        {searchFeature && (
          <SearchField
            searchQuery={searchFeature.searchQuery}
            setSearchQuery={searchFeature.setSearchQuery}
            testId={tableId}
            gotoPage={gotoPage}
          />
        )}
        {sortingFeature && (
          <SortingOptions
            sortingOption={sortingFeature?.sortingOption}
            options={sortingFeature.options}
            defaultSortingOption={sortingFeature.defaultSortingOption}
            setSortingOption={sortingFeature.setSortingOption}
            gotoPage={gotoPage}
            isDisabled={isDisabled}
          />
        )}
        {filterFeature &&
          filterFeature.map(filter => (
            <StatusFilters
              key={filter.filterName}
              filters={filter.filters}
              filteredOption={filter.filteredOption}
              filteredOptions={filter.filteredOptions}
              setFilteredOption={filter.setFilteredOption}
              setFilteredOptions={filter.setFilteredOptions}
              filterName={filter.filterName}
              tableId={tableId}
              topOptions={filter.topOptions}
              gotoPage={gotoPage}
              placeholder={filter.placeholder}
              defaultOption={filter?.defaultOption}
              isDisabled={isDisabled}
              minWidth={filter.minWidth}
              width={filter.width}
              isMulti={filter.isMulti}
              cacheUniqs={filter.cacheUniqs}
            />
          ))}
        {!hideResultCount && (
          <span className="pageInfo" data-testid={`${tableId}__pagesInfo`}>
            Showing {formatDisplayCount(pagesFrom)} to{' '}
            {formatDisplayCount(pagesUntil)} of{' '}
            {formatDisplayCount(totalElementsCount)}
          </span>
        )}
      </div>

      <div className="buttons-section">
        {customButtons || (
          <>
            {otherButton && (
              <button
                onClick={otherButton.onButtonClick}
                type="button"
                className="btn btn-outline-primary"
                disabled={isDisabled}
              >
                <FontAwesomeIcon icon={faFileImport} /> {otherButton.buttonText}
              </button>
            )}
            {addButton && (
              <Button
                onClick={addButton.onAddButtonClick}
                type="button"
                className="btn btn-primary btn-create new"
                disabled={addButton.isDisabled || isDisabled}
                loading={addButton.isLoading}
                {...addButtonTestId}
              >
                <FontAwesomeIcon icon={faPlus} /> {addButton.addButtonText}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TopBar;
